import { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { MdInsertChartOutlined } from "react-icons/md";
import Skeleton from "components/skeleton/Skeleton";
import EmptyState from "components/states/EmptyState";
import { useDarkmode } from "hooks/useTheme";
import { getBaseOptions, getModeColors, heights, mergeOptions } from "lib/chartTheme";

const CIRCULAR_TYPES = new Set(["pie", "donut", "polarArea", "radialBar"]);

const isSeriesEmpty = (series) => {
  if (!Array.isArray(series) || series.length === 0) return true;
  if (series.every((item) => typeof item === "number")) {
    return series.every((n) => !n);
  }
  return series.every((item) => !Array.isArray(item?.data) || item.data.length === 0);
};

/**
 * Themed ApexCharts wrapper. Options merge onto `getBaseOptions(isDark)`.
 *
 * @param {object} props
 * @param {string} [props.type="line"] Apex chart type ("line", "area", "bar", "donut", …).
 * @param {Array} props.series Apex series.
 * @param {object} [props.options] Per-chart Apex options; deep-merged over the theme.
 *   Pass a memoized/stable object to avoid re-rendering the chart each render.
 * @param {"sm"|"md"|"lg"|number} [props.height="md"] Named height (180/280/350) or pixels.
 * @param {boolean} [props.loading=false] Show a skeleton of the chart's height.
 * @param {boolean} [props.empty] Force the empty state; defaults to "series has no data".
 * @param {string} [props.emptyText="No data yet"]
 * @param {string} [props.className] Wrapper classes.
 */
const Chart = ({
  type = "line",
  series,
  options,
  height = "md",
  loading = false,
  empty,
  emptyText = "No data yet",
  className = "",
}) => {
  const isDark = Boolean(useDarkmode()?.darkmode);
  const pixelHeight = typeof height === "number" ? height : heights[height] ?? heights.md;

  const mergedOptions = useMemo(() => {
    let base = getBaseOptions(isDark);
    if (CIRCULAR_TYPES.has(type)) {
      // Slice separators should match the card behind the chart.
      base = mergeOptions(base, { stroke: { colors: [getModeColors(isDark).surface] } });
    }
    return mergeOptions(base, options);
  }, [isDark, type, options]);

  const showEmpty = empty ?? isSeriesEmpty(series);

  if (loading) {
    return (
      <div className={className} style={{ height: pixelHeight }}>
        <Skeleton className="h-full w-full rounded-control" />
      </div>
    );
  }

  if (showEmpty) {
    return (
      <div
        className={`flex items-center justify-center ${className}`.trim()}
        style={{ height: pixelHeight }}
      >
        <EmptyState variant="inline" icon={MdInsertChartOutlined} title={emptyText} />
      </div>
    );
  }

  return (
    <div className={className}>
      <ReactApexChart type={type} series={series} options={mergedOptions} height={pixelHeight} />
    </div>
  );
};

export default Chart;
